import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaCreditCard,
  FaShippingFast,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSort,
  FaChevronLeft,
  FaChevronRight,
  FaSync,
  FaExclamationTriangle,
  FaUser,
  FaShoppingCart
} from 'react-icons/fa';
import './OrderManagement.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    orderType: '',
    dateRange: '',
    startDate: '',
    endDate: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [bulkAction, setBulkAction] = useState('');

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [pagination.currentPage, sortConfig, filters]);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [orders, searchTerm, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...(filters.status && { status: filters.status }),
        ...(filters.paymentStatus && { paymentStatus: filters.paymentStatus }),
        ...(filters.orderType && { orderType: filters.orderType }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      });

      const response = await axios.get(`${API_URL}/api/admin/orders?${params}`);
      const { orders: fetchedOrders, pagination: paginationData, total } = response.data;

      console.log('Fetched orders:', { fetchedOrders, paginationData, total });

      setOrders(fetchedOrders || []);
      setPagination(prev => ({
        ...prev,
        totalItems: total || 0,
        totalPages: paginationData?.pages || Math.ceil((total || 0) / prev.itemsPerPage)
      }));

    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("Failed to fetch orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...orders];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply date range filter
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    setFilteredOrders(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusUpdate = async (orderId, newStatus, note = '') => {
    try {
      await axios.put(`${API_URL}/api/admin/orders/${orderId}`, {
        orderStatus: newStatus,
        notes: note
      });

      // Refresh orders
      await fetchOrders();
      
      // Show success message
      showSuccessMessage(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating order status:", error);
      showErrorMessage("Failed to update order status");
    }
  };

  const handlePaymentStatusUpdate = async (orderId, paymentStatus, paymentDetails = {}) => {
    try {
      await axios.put(`${API_URL}/api/admin/orders/${orderId}`, {
        paymentStatus,
        paymentDetails
      });

      await fetchOrders();
      showSuccessMessage(`Payment status updated to ${paymentStatus}`);
    } catch (error) {
      console.error("Error updating payment status:", error);
      showErrorMessage("Failed to update payment status");
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedOrders.length === 0) {
      showErrorMessage("Please select orders and choose an action");
      return;
    }

    try {
      setLoading(true);
      const promises = selectedOrders.map(orderId => {
        switch (bulkAction) {
          case 'mark-shipped':
            return handleStatusUpdate(orderId, 'shipped', 'Bulk update: Marked as shipped');
          case 'mark-delivered':
            return handleStatusUpdate(orderId, 'delivered', 'Bulk update: Marked as delivered');
          case 'send-email':
            return axios.post(`${API_URL}/api/admin/orders/${orderId}/send-notification`);
          default:
            return Promise.resolve();
        }
      });

      await Promise.all(promises);
      setSelectedOrders([]);
      setBulkAction('');
      showSuccessMessage(`Bulk action completed for ${selectedOrders.length} orders`);
      
    } catch (error) {
      console.error("Error in bulk action:", error);
      showErrorMessage("Some operations failed. Please check and try again.");
    } finally {
      setLoading(false);
    }
  };

  const exportOrders = () => {
    try {
      const csvContent = [
        'Order Number,Customer,Email,Status,Payment Status,Amount,Date,Items',
        ...filteredOrders.map(order => [
          order.orderNumber,
          order.userName,
          order.userEmail,
          order.orderStatus,
          order.paymentStatus,
          order.totalAmount,
          new Date(order.createdAt).toLocaleDateString(),
          order.items?.map(item => `${item.name} (${item.quantity})`).join('; ')
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      showSuccessMessage('Orders exported successfully');
    } catch (error) {
      showErrorMessage('Failed to export orders');
    }
  };

  const showSuccessMessage = (message) => {
    // You can implement a toast notification system here
    alert(message);
  };

  const showErrorMessage = (message) => {
    // You can implement a toast notification system here
    alert(message);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <FaCheckCircle className="status-icon success" />;
      case 'shipped': return <FaShippingFast className="status-icon info" />;
      case 'confirmed': return <FaCheckCircle className="status-icon warning" />;
      case 'processing': return <FaClock className="status-icon pending" />;
      case 'cancelled': return <FaTimesCircle className="status-icon error" />;
      default: return <FaClock className="status-icon" />;
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <FaCheckCircle className="payment-icon success" />;
      case 'pending': return <FaClock className="payment-icon warning" />;
      case 'failed': return <FaTimesCircle className="payment-icon error" />;
      default: return <FaCreditCard className="payment-icon" />;
    }
  };

  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      
      let date;
      if (typeof dateString === 'object' && dateString.toDate) {
        // Firestore timestamp
        date = dateString.toDate();
      } else if (typeof dateString === 'string') {
        // ISO string
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        // Date object
        date = dateString;
      } else {
        return 'N/A';
      }
      
      return date.toLocaleString();
    };

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content order-details-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Order Details - {order.orderNumber || order.id}</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="order-details-grid">
              <div className="detail-section customer-info">
                <h3><FaUser /> Customer Information</h3>
                <div className="info-row">
                  <span className="label">Name:</span>
                  <span className="value">{order.userName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{order.userEmail}</span>
                </div>
                <div className="info-row">
                  <span className="label">Order Date:</span>
                  <span className="value">{formatDate(order.createdAt)}</span>
                </div>
                <div className="info-row">
                  <span className="label">Order Type:</span>
                  <span className="value">{order.orderType || 'Product'}</span>
                </div>
              </div>

              <div className="detail-section order-status">
                <h3><FaClock /> Order Status</h3>
                <div className="status-controls">
                  <select 
                    value={order.orderStatus} 
                    onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="processing">Processing</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {getStatusIcon(order.orderStatus)}
                </div>
              </div>

              <div className="detail-section payment-info">
                <h3><FaCreditCard /> Payment Information</h3>
                <div className="payment-controls">
                  <select 
                    value={order.paymentStatus} 
                    onChange={(e) => handlePaymentStatusUpdate(order.id, e.target.value)}
                    className="payment-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                  {getPaymentStatusIcon(order.paymentStatus)}
                </div>
                <div className="info-row">
                  <span className="label">Amount:</span>
                  <span className="value amount">₹{order.totalAmount?.toLocaleString()}</span>
                </div>
                <div className="info-row">
                  <span className="label">Method:</span>
                  <span className="value">{order.paymentMethod}</span>
                </div>
                <div className="info-row">
                  <span className="label">Gateway:</span>
                  <span className="value">{order.paymentDetails?.gateway || 'N/A'}</span>
                </div>
                {order.paymentId && (
                  <div className="info-row">
                    <span className="label">Transaction ID:</span>
                    <span className="value">
                      {order.paymentId.length > 8 ? `****${order.paymentId.slice(-4)}` : '****'}
                    </span>
                  </div>
                )}
                {order.paymentDetails?.paymentMethod && (
                  <div className="info-row">
                    <span className="label">Payment Method:</span>
                    <span className="value">{order.paymentDetails.paymentMethod}</span>
                  </div>
                )}
              </div>

              <div className="detail-section order-items">
                <h3><FaShoppingCart /> Order Items</h3>
                <div className="items-list">
                  {order.items?.map((item, index) => (
                    <div key={index} className="item-row">
                      <div className="item-details">
                        <span className="item-name">{item.name}</span>
                        {item.type === 'workshop' && item.workshopDetails && (
                          <div className="workshop-details">
                            <small>Date: {item.workshopDetails.date}</small>
                            <small>Time: {item.workshopDetails.time}</small>
                            <small>Instructor: {item.workshopDetails.instructor}</small>
                          </div>
                        )}
                      </div>
                      <span className="item-quantity">Qty: {item.quantity}</span>
                      <span className="item-price">₹{(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="total-amount">
                  <strong>Total: ₹{order.totalAmount?.toLocaleString()}</strong>
                </div>
              </div>

              {order.shippingAddress && (
                <div className="detail-section shipping-info">
                  <h3><FaShippingFast /> Shipping Address</h3>
                  <div className="address">
                    <p><strong>{order.shippingAddress.firstName} {order.shippingAddress.lastName}</strong></p>
                    <p>{order.shippingAddress.address}</p>
                    <p>{order.shippingAddress.city}, {order.shippingAddress.state}</p>
                    <p>{order.shippingAddress.zipCode}, {order.shippingAddress.country}</p>
                    {order.shippingAddress.phone && <p>Phone: {order.shippingAddress.phone}</p>}
                  </div>
                </div>
              )}

              {order.statusHistory && order.statusHistory.length > 0 && (
                <div className="detail-section status-history">
                  <h3><FaClock /> Status History</h3>
                  <div className="history-list">
                    {order.statusHistory.map((status, index) => (
                      <div key={index} className="history-item">
                        <div className="history-status">{status.status}</div>
                        <div className="history-date">
                          {new Date(status.timestamp).toLocaleString()}
                        </div>
                        {status.note && <div className="history-note">{status.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-primary"
              onClick={() => {
                // Send order notification email
                axios.post(`${API_URL}/api/admin/orders/${order.id}/send-notification`)
                  .then(() => showSuccessMessage('Email sent successfully'))
                  .catch(() => showErrorMessage('Failed to send email'));
              }}
            >
              <FaEnvelope /> Send Email
            </button>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && orders.length === 0) {
    return (
      <div className="order-management">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="order-management">
        <div className="error-state">
          <FaExclamationTriangle />
          <h3>Error Loading Orders</h3>
          <p>{error}</p>
          <button onClick={fetchOrders} className="btn btn-primary">
            <FaSync /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-management">
      <div className="page-header">
        <div className="header-content">
          <h1>Order Management</h1>
          <p>Manage all customer orders and track their progress</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={exportOrders}>
            <FaDownload /> Export CSV
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-filters-section">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by order number, customer name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All Statuses</option>
                <option value="processing">Processing</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select
                value={filters.paymentStatus}
                onChange={(e) => setFilters({...filters, paymentStatus: e.target.value})}
              >
                <option value="">All Payment Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </select>

              <select
                value={filters.orderType}
                onChange={(e) => setFilters({...filters, orderType: e.target.value})}
              >
                <option value="">All Order Types</option>
                <option value="product">Products</option>
                <option value="workshop">Workshops</option>
                <option value="mixed">Mixed</option>
              </select>

              <input
                type="date"
                placeholder="Start Date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />

              <input
                type="date"
                placeholder="End Date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />

              <button 
                className="btn btn-secondary"
                onClick={() => setFilters({
                  status: '', paymentStatus: '', orderType: '', 
                  dateRange: '', startDate: '', endDate: ''
                })}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedOrders.length > 0 && (
        <div className="bulk-actions">
          <span>{selectedOrders.length} orders selected</span>
          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
          >
            <option value="">Choose Action</option>
            <option value="mark-shipped">Mark as Shipped</option>
            <option value="mark-delivered">Mark as Delivered</option>
            <option value="send-email">Send Notification Email</option>
          </select>
          <button className="btn btn-primary" onClick={handleBulkAction}>
            Apply Action
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => setSelectedOrders([])}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Orders Table */}
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrders(filteredOrders.map(order => order.id));
                    } else {
                      setSelectedOrders([]);
                    }
                  }}
                />
              </th>
              <th onClick={() => handleSort('orderNumber')}>
                Order Number <FaSort />
              </th>
              <th onClick={() => handleSort('userName')}>
                Customer <FaSort />
              </th>
              <th onClick={() => handleSort('totalAmount')}>
                Amount <FaSort />
              </th>
              <th>Status</th>
              <th>Payment</th>
              <th onClick={() => handleSort('createdAt')}>
                Date <FaSort />
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr 
                key={order.id} 
                className={selectedOrders.includes(order.id) ? 'selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrders([...selectedOrders, order.id]);
                      } else {
                        setSelectedOrders(selectedOrders.filter(id => id !== order.id));
                      }
                    }}
                  />
                </td>
                <td>
                  <span className="order-number">{order.orderNumber}</span>
                  <small className="order-type">{order.orderType}</small>
                </td>
                <td>
                  <div className="customer-cell">
                    <span className="customer-name">{order.userName}</span>
                    <small className="customer-email">{order.userEmail}</small>
                  </div>
                </td>
                <td className="amount-cell">
                  ₹{order.totalAmount?.toLocaleString()}
                </td>
                <td>
                  <div className="status-cell">
                    {getStatusIcon(order.orderStatus)}
                    <span className={`status-badge ${order.orderStatus}`}>
                      {order.orderStatus}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="payment-cell">
                    {getPaymentStatusIcon(order.paymentStatus)}
                    <span className={`payment-badge ${order.paymentStatus}`}>
                      {order.paymentStatus}
                    </span>
                  </div>
                </td>
                <td className="date-cell">
                  {new Date(order.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowOrderDetails(true);
                      }}
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => {
                        const newStatus = prompt("Enter new status:", order.orderStatus);
                        if (newStatus) handleStatusUpdate(order.id, newStatus);
                      }}
                      title="Update Status"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this order?")) {
                          // Handle delete
                        }
                      }}
                      title="Delete Order"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredOrders.length === 0 && !loading && (
          <div className="empty-state">
            <FaShippingFast className="empty-icon" />
            <h3>No orders found</h3>
            <p>No orders match your current filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={pagination.currentPage === 1}
            onClick={() => setPagination(prev => ({...prev, currentPage: prev.currentPage - 1}))}
          >
            <FaChevronLeft /> Previous
          </button>
          
          <span className="pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
            ({pagination.totalItems} total orders)
          </span>
          
          <button
            className="btn btn-outline"
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => setPagination(prev => ({...prev, currentPage: prev.currentPage + 1}))}
          >
            Next <FaChevronRight />
          </button>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderDetails && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default OrderManagement; 